import type { ComponentProps, ElementType } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const contentContainerVariants = cva('container', {
  variants: {
    padding: {
      default: 'section-padding',
      lg: 'section-padding-lg',
      xl: 'section-padding-xl',
      none: '',
    },
  },
  defaultVariants: {
    padding: 'default',
  },
});

interface ContentContainerProps<T extends ElementType = 'section'>
  extends VariantProps<typeof contentContainerVariants> {
  as?: T;
  className?: string;
  /**
   * When true, skip `container` width/padding. If `className` is omitted, render a fragment.
   * If `className` is set, render a plain `div` with those classes (e.g. stacking `z-index`)
   * so layout utilities still have a node when full-bleed.
   */
  passthrough?: boolean;
}

function ContentContainer<T extends ElementType = 'section'>({
  as,
  className,
  padding,
  passthrough,
  ...props
}: ContentContainerProps<T> & Omit<ComponentProps<T>, keyof ContentContainerProps<T>>) {
  if (passthrough) {
    const Component = as || 'div';

    if (className) {
      return <Component className={className} {...props} />;
    }

    return <>{props.children}</>;
  }

  const Component = as || 'section';
  return (
    <Component
      data-slot="content-container"
      className={cn(contentContainerVariants({ padding }), className)}
      {...props}
    />
  );
}

export { ContentContainer, type ContentContainerProps };
